import { z } from "zod";

import { VibeGitPushNoDeployReasonSchema } from "../../../shared/domain/vibe/git-push-no-deploy-reasons";
import { VibeAuditGitPushNoDeployReasonReadSchema } from "./read-vocabularies.schema";

/**
 * Emitted when a push was accepted, the ref advanced, and NOTHING was built.
 *
 * Until this row existed, that outcome was invisible three times over: the
 * ingest use case returned it as a success, the receive-hook controller
 * deliberately excluded it from logging, and the git server's post-receive
 * hook discarded the response. The console then rendered the app as "Never
 * deployed" — byte-identical to an app nobody had pushed to yet. An app whose
 * project link was missing could absorb pushes for hours and look idle.
 *
 * Written ONLY for the reasons in `VIBE_GIT_PUSH_UNEXPECTED_NO_DEPLOY_REASONS`.
 * Pushes to a non-deploy ref and branch deletions are ordinary development
 * traffic and write no row — a repository with any branch discipline produces
 * far more of those than deploying pushes, and they would bury this event
 * under its own repetitions. Same reasoning as `APP_EDGE_UNROUTED` writing on
 * the transition only.
 *
 * The payload has to stand alone. The ref moves on, apps get attached, deploy
 * branches get corrected — so the row carries what was true at push time, not
 * pointers to state that will have changed by the time anyone reads it.
 *
 * `vibeAppId` on the row is NULL by construction: the subject is the git
 * project and the ref, and in the case that matters there is no app to point
 * at. `actorUserId` is the pusher when the git server mapped one.
 */
/**
 * Caps on the two unbounded lists in the `GIT_PUSH_NO_DEPLOY` payload.
 * Exported because the writer has to truncate to exactly these numbers —
 * a cap restated at the call site is a cap that drifts, and the drift shows
 * up as an audit write that throws payload validation in production.
 */
export const VIBE_AUDIT_GIT_PUSH_NO_DEPLOY_LIST_CAP = 50;
export const VIBE_AUDIT_GIT_PUSH_NO_DEPLOY_REFUSAL_REASON_CAP = 500;

export const VibeAuditGitPushNoDeployPayloadSchema = z.object({
  eventType: z.literal("GIT_PUSH_NO_DEPLOY"),
  vibeGitProjectId: z.string().uuid(),
  /// Fully-qualified ref the push targeted (e.g. `refs/heads/main`). Same
  /// bound as the receive hook's own body schema.
  refName: z.string().min(1).max(255),
  /// The commit that landed. Same bounds as the DEPLOYMENT_TRIGGERED
  /// payload's `triggerSha`.
  newSha: z.string().min(1).max(64),
  reason: VibeGitPushNoDeployReasonSchema,
  /// The deploy refs of every app attached to this project at push time —
  /// the answer to "then what SHOULD I have pushed to?". Empty for
  /// `no_apps_attached`, which is the honest value: nothing was watching.
  /// Bounded because a project's app count is not, and an audit row must not
  /// be able to grow without limit.
  watchedRefs: z.array(z.string().min(1).max(255)).max(VIBE_AUDIT_GIT_PUSH_NO_DEPLOY_LIST_CAP),
  /// The apps whose deploy trigger was refused, with the rationale. Only
  /// populated for `all_deploys_refused`, and it is the ONLY durable record
  /// of those reasons: a refused trigger writes no deployment row, so
  /// without this the ledger could say a push built nothing but never why.
  refusals: z
    .array(
      z.object({
        vibeAppId: z.string().uuid(),
        reason: z.string().min(1).max(VIBE_AUDIT_GIT_PUSH_NO_DEPLOY_REFUSAL_REASON_CAP)
      })
    )
    .max(VIBE_AUDIT_GIT_PUSH_NO_DEPLOY_LIST_CAP)
});
export type VibeAuditGitPushNoDeployPayload = z.infer<typeof VibeAuditGitPushNoDeployPayloadSchema>;

/**
 * The READ side of this family's branches: what the audit feed's readers parse.
 * Each one widens only its enum fields, to the lenient read vocabularies in
 * `read-vocabularies.schema.ts` — a value a newer backend writes reaches an
 * older reader as that field's unlisted value instead of failing the feed page.
 * The emitting branches above stay strict.
 */
export const VibeAuditGitPushNoDeployReadPayloadSchema =
  VibeAuditGitPushNoDeployPayloadSchema.extend({
    reason: VibeAuditGitPushNoDeployReasonReadSchema
  });
